<template>
    <tailwind-layout :title="property.full_address">
        <div class="bg-white rounded-lg shadow-sm -mt-10 -mx-2">
            <!-- Property Header Section -->
            <div class="relative">
                <div class="flex flex-col lg:flex-row lg:items-stretch gap-4">
                    <!-- Property Image -->
                    <div class="w-full lg:w-1/2 h-52 lg:h-72 bg-gray-200 overflow-hidden flex items-center justify-center">
                        <img
                            :src="propertyImage"
                            :alt="property.full_address"
                            class="h-full w-auto object-contain"
                        />
                    </div>

                    <!-- Property Details -->
                    <div class="w-full lg:w-1/2 p-6 lg:p-8 flex flex-col justify-between">
                        <div>
                            <div class="mb-4">
                                <h1 class="text-3xl font-bold text-gray-900">
                                    {{ formattedPrice }}/month
                                </h1>
                                <p class="text-base text-gray-600 mb-4">{{ property.full_address }}</p>
                            </div>

                            <div class="flex items-center gap-4 text-sm text-gray-600 mb-4">
                                <span v-if="property.sqft">{{ property.sqft }} sq.ft</span>
                                <span v-if="formattedBedsBaths">{{ formattedBedsBaths }}</span>
                            </div>

                            <!-- Property Description -->
                            <div v-if="property.description" class="mb-4">
                                <h3 class="text-sm font-semibold text-gray-900 mb-1">Property Description</h3>
                                <p class="text-sm text-gray-600 leading-normal whitespace-pre-wrap">
                                    <span v-if="!showFullDescription">{{ truncatedDescription }}</span>
                                    <span v-else>{{ property.description }}</span>
                                </p>
                                <button
                                    v-if="property.description && property.description.length > 100"
                                    @click="showFullDescription = !showFullDescription"
                                    class="mt-1 text-xs text-blue-600 hover:text-blue-800"
                                >
                                    {{ showFullDescription ? 'See Less' : 'See More' }}
                                </button>
                            </div>

                            <!-- Amenities -->
                            <div v-if="allAmenities.length > 0" class="mb-4">
                                <h3 class="text-sm font-semibold text-gray-900 mb-1">Amenities</h3>
                                <div class="flex flex-wrap gap-1.5">
                                    <span
                                        v-for="amenity in displayedAmenities"
                                        :key="amenity.key"
                                        class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                                    >
                                        {{ amenity.name }}
                                    </span>
                                </div>
                                <button
                                    v-if="allAmenities.length > maxDisplayedAmenities"
                                    @click="showAllAmenities = !showAllAmenities"
                                    class="mt-1.5 text-xs text-blue-600 hover:text-blue-800"
                                >
                                    {{ showAllAmenities ? 'Show Less' : 'Show All Amenities' }}
                                </button>
                            </div>

                            <!-- Landlord / Property manager -->
                            <div v-if="landlord" class="mb-4 p-4 rounded-lg border border-gray-200 bg-gray-50">
                                <h3 class="text-sm font-semibold text-gray-900 mb-2">Landlord / Property manager</h3>
                                <dl class="space-y-1 text-sm text-gray-600">
                                    <div v-if="landlord.name" class="flex gap-2">
                                        <dt class="font-medium text-gray-700 min-w-[4rem]">Name</dt>
                                        <dd>{{ landlord.name }}</dd>
                                    </div>
                                    <div v-if="landlord.email" class="flex gap-2">
                                        <dt class="font-medium text-gray-700 min-w-[4rem]">Email</dt>
                                        <dd>
                                            <a :href="`mailto:${landlord.email}`" class="text-rent-risk-blue-600 hover:underline">{{ landlord.email }}</a>
                                        </dd>
                                    </div>
                                    <div v-if="landlord.phone" class="flex gap-2">
                                        <dt class="font-medium text-gray-700 min-w-[4rem]">Phone</dt>
                                        <dd>
                                            <a :href="`tel:${landlord.phone}`" class="text-rent-risk-blue-600 hover:underline">{{ landlord.phone }}</a>
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Navigation Tabs -->
            <div class="px-6 pt-4">
                <div class="grid grid-cols-1 sm:hidden">
                    <select
                        aria-label="Select a tab"
                        :value="currentTab"
                        @change="handleTabChange($event.target.value)"
                        class="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600"
                    >
                        <option v-for="tab in tabs" :key="tab.slug" :value="tab.slug">{{ tab.name }}</option>
                    </select>
                    <font-awesome-icon
                        :icon="faChevronDown"
                        class="pointer-events-none col-start-1 row-start-1 mr-2 h-5 w-5 self-center justify-self-end text-gray-500"
                        aria-hidden="true"
                    />
                </div>
                <div class="hidden sm:block">
                    <div class="border-b border-gray-200">
                        <nav class="-mb-px flex space-x-8" aria-label="Tabs">
                            <button
                                v-for="tab in tabs"
                                :key="tab.slug"
                                @click="handleTabChange(tab.slug)"
                                :class="[
                                    currentTab === tab.slug
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                                    'group inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium cursor-pointer'
                                ]"
                                :aria-current="currentTab === tab.slug ? 'page' : undefined"
                            >
                                <font-awesome-icon
                                    :icon="tab.icon"
                                    :class="[
                                        currentTab === tab.slug
                                            ? 'text-blue-500'
                                            : 'text-gray-400 group-hover:text-gray-500',
                                        '-ml-0.5 mr-2 h-5 w-5'
                                    ]"
                                    aria-hidden="true"
                                />
                                <span>{{ tab.name }}</span>
                            </button>
                        </nav>
                    </div>
                </div>
            </div>

            <!-- Content Area -->
            <div class="p-6">
                <component
                    :is="currentTabComponent"
                    :property="property"
                    :tab-data="tabData"
                    :landlord="landlord"
                    :terms="terms"
                />
            </div>
        </div>
    </tailwind-layout>
</template>

<script setup>
import { appUrl } from '@/appUrl'
import { ref, computed } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faChevronDown, faCommentsDollar, faWrench, faShieldExclamation, faSquareDollar } from '@fortawesome/pro-light-svg-icons'
import RentPaymentsTab from './Tabs/Applicant/RentPaymentsTab.vue'
import MaintenanceTab from './Tabs/Applicant/MaintenanceTab.vue'
import RentersInsuranceTab from './Tabs/Applicant/RentersInsuranceTab.vue'
import VaLoanTab from './Tabs/Applicant/VaLoanTab.vue'
import { mergedAmenities } from './propertyAmenities'

const route = (typeof window !== 'undefined' && window.route) || ((name, ...params) => {
    console.warn('Route function not available')
    return '#'
})

const props = defineProps({
    property: {
        type: Object,
        required: true
    },
    landlord: {
        type: Object,
        default: null
    },
    tab: {
        type: String,
        default: 'rent-payments'
    },
    tabData: {
        type: Object,
        default: () => ({})
    },
    terms: {
        type: String,
        default: null
    },
    version: {
        type: String,
        default: null
    },
})

const page = usePage()
const currentTab = computed(() => props.tab || 'rent-payments')

const tabs = [
    { name: 'Payments', icon: faCommentsDollar, slug: 'rent-payments', component: RentPaymentsTab },
    { name: 'Maintenance', icon: faWrench, slug: 'maintenance', component: MaintenanceTab },
    { name: 'Insurance', icon: faShieldExclamation, slug: 'renters-insurance', component: RentersInsuranceTab },
    { name: 'Mortgage', icon: faSquareDollar, slug: 'va-loan', component: VaLoanTab },
]

const currentTabComponent = computed(() => {
    const activeTab = tabs.find(t => t.slug === currentTab.value)
    return activeTab ? activeTab.component : RentPaymentsTab
})

const clearFlashMessages = () => {
    if (page?.props?.flash) {
        page.props.flash.success = null
        page.props.flash.error = null
    }
}

const handleTabChange = (tabSlug) => {
    clearFlashMessages()
    router.get(route('my-property'), { tab: tabSlug }, {
        preserveState: true,
        preserveScroll: true,
    })
}

const propertyImage = computed(() => {
    return props.property.photo
        ? (props.property.photo.startsWith('http') ? props.property.photo : appUrl(`/storage/${props.property.photo}`))
        : '/default-property-image.jpg'
})

const formattedPrice = computed(() => {
    return props.property.rent
        ? `$${Number(props.property.rent).toLocaleString()}`
        : 'N/A'
})

const formattedBedsBaths = computed(() => {
    const beds = props.property.beds ?? null
    const baths = props.property.baths ?? null

    if (beds === null && baths === null) return null

    const bedsStr = beds !== null ? `${beds} bed${beds !== 1 ? 's' : ''}` : null
    const bathsStr = baths !== null ? `${baths} bath${baths !== 1 ? 's' : ''}` : null

    if (bedsStr && bathsStr) {
        return `${bedsStr}/${bathsStr}`
    } else if (bedsStr) {
        return bedsStr
    } else if (bathsStr) {
        return bathsStr
    }

    return null
})

const showFullDescription = ref(false)
const showAllAmenities = ref(false)
const maxDisplayedAmenities = 4

const truncatedDescription = computed(() => {
    if (!props.property.description) return ''
    return props.property.description.length > 100
        ? props.property.description.substring(0, 100) + '...'
        : props.property.description
})

const allAmenities = computed(() => mergedAmenities(props.property))

const displayedAmenities = computed(() => {
    if (showAllAmenities.value) {
        return allAmenities.value
    }
    return allAmenities.value.slice(0, maxDisplayedAmenities)
})
</script>
